import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StoreIcon, ArrowRightIcon, CopyIcon, CheckIcon, ExternalLinkIcon, RocketIcon, PencilIcon, XIcon } from 'lucide-react';
import { useBusiness, BUSINESS_TYPE_LABEL } from './BusinessContext';
import { useShopStudio } from '../contexts/ShopStudioContext';
import { ShopStudio, StudioTab } from './settings/ShopStudio';
import { WidgetStudio } from './widget/WidgetStudio';
import { WidgetPreview } from './widget/WidgetPreview';
import { StorefrontViewport } from './settings/StorefrontViewport';
import { DomainModal } from './settings/DomainModal';
import { SeoModal } from './settings/SeoModal';
import { seoStatus, seoStatusLabel } from '../utils/seo';
import { UpgradePlanModal } from './UpgradePlanModal';
type OpenStudio = {
  kind: 'shop';
  tab: StudioTab;
} | {
  kind: 'widget';
} | null;

/**
 * My Shop — the merchant's control centre for everything customers see. It
 * answers four questions at a glance: what do I have, what is live, what link
 * do I share, and what should I do next. Every detailed change happens in the
 * studios this page launches.
 */
export function MyShop() {
  const {
    hub,
    currentPlanId,
    subscribePackage
  } = useBusiness();
  const {
    config,
    widget,
    update,
    updateWidget,
    undo,
    redo,
    canUndo,
    canRedo,
    resetTheme,
    toast,
    dirty,
    markPublished
  } = useShopStudio();
  const [open, setOpen] = useState<OpenStudio>(null);
  const [upgrade, setUpgrade] = useState(false);
  const [preview, setPreview] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  const [copied, setCopied] = useState<'link' | null>(null);
  const onScale = currentPlanId === 'Scale';
  const seo = seoStatus(config.seo);
  const talabUrl = `talab.app/${config.handle}`;
  const primaryUrl = config.customDomain || talabUrl;
  const state: 'draft' | 'changes' | 'live' = !config.published ? 'draft' : dirty ? 'changes' : 'live';
  const copyLink = () => {
    navigator.clipboard?.writeText(`https://${primaryUrl}`);
    setCopied('link');
    window.setTimeout(() => setCopied(null), 1800);
  };

  /* ----------------------------------------------------------- The studios */
  if (open?.kind === 'shop') return <ShopStudio config={config} update={update} initialTab={open.tab} canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} onResetTheme={resetTheme} backLabel="My Shop" onClose={() => setOpen(null)} onSave={() => {
    if (!config.showTalabBranding && !onScale) {
      update({
        showTalabBranding: true
      });
      toast('Upgrade to Scale to save a header without Talab branding');
      return;
    }
    toast('Shop changes saved');
  }} data-id="element-2857" data-mp="31vx4fkr0vg4e" />;
  if (open?.kind === 'widget') return <>
        <WidgetStudio widget={widget} updateWidget={updateWidget} config={config} canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} canRemoveBranding={onScale} onUpgrade={() => setUpgrade(true)} onClose={() => setOpen(null)} onSave={() => {
      if (!widget.showTalabBranding && !onScale) {
        updateWidget({
          showTalabBranding: true
        });
        toast('Upgrade to Scale to save a white-labelled chat');
        return;
      }
      toast('Website chat saved');
    }} data-id="element-2858" data-mp="h2pdndszdoqw" />
        {upgrade && <UpgradePlanModal initialPlan="Scale" onClose={() => setUpgrade(false)} data-id="element-2859" data-mp="3phzekil4wnx3" />}
      </>;

  /* ------------------------------------------------------- Control centre */
  return <div className="w-full max-w-6xl mx-auto pb-28" data-id="element-2860" data-mp="3gim53px0fzcd">
      <header className="mb-5 lg:mb-6" data-id="element-2861" data-mp="38es67p1lotbk">
        <p className="text-sm text-slate-500" data-id="element-2862" data-mp="38hxvb4a8bv7a">
          Manage how customers see and interact with your business.
        </p>
      </header>

      {/* ---------------------------------------------------- Status summary */}
      <motion.section initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22,
      ease: 'easeOut'
    }} className="rounded-[28px] bg-white border border-slate-200/80 p-6 lg:p-8 mb-6 lg:mb-8" data-id="element-2863" data-mp="2uoordj51w1w5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-2864" data-mp="2frxsrc37au7l">
          <div className="flex items-center gap-4 min-w-0 flex-1" data-id="element-2865" data-mp="2bqci7xe52s0k">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-2866" data-mp="dzt01ydvnsvt">
              {config.logo ? <img src={config.logo} alt="" className="w-full h-full object-contain p-2" data-id="element-2867" data-mp="9ypldf7kg2w6" /> : <StoreIcon className="w-6 h-6 text-slate-300" data-id="element-2868" data-mp="1wtmgq06kux0a" />}
            </div>
            <div className="min-w-0" data-id="element-2869" data-mp="21mp1pjxagy4v">
              <div className="flex items-center gap-3 flex-wrap" data-id="element-2870" data-mp="21nqmwh80r10b">
                <h2 className="font-display font-bold text-xl text-dark-navy truncate" data-id="element-2871" data-mp="25lxvgosbww0f">
                  {config.publicName}
                </h2>
                <StatusChip state={state} data-id="element-2872" data-mp="1dw1r2qvznuem" />
              </div>
              <p className="text-sm text-slate-500 mt-1" data-id="element-2873" data-mp="1dwl72222w8q0">
                {BUSINESS_TYPE_LABEL[hub?.type ?? 'generic']}
              </p>
              <div className="flex items-center gap-1 mt-2 flex-wrap" data-id="element-2874" data-mp="1mwq69ox4c0t3">
                <button onClick={copyLink} title="Copy shop link" className="group inline-flex items-center gap-2 text-sm font-semibold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-2875" data-mp="1my165dng0oex">
                  <span className="truncate" data-id="element-2876" data-mp="y7u51kkpocfq">{primaryUrl}</span>
                  {copied === 'link' ? <CheckIcon className="w-3.5 h-3.5 text-success" data-id="element-2877" data-mp="ua4sc8j5itn0" /> : <CopyIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2878" data-mp="1871s4zh8zlg1" />}
                </button>
                <button onClick={() => setDomainOpen(true)} className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2879" data-mp="fjp56o76dbm7">
                  <PencilIcon className="w-3 h-3" data-id="element-2880" data-mp="kgxq2wjpjtr5" />
                  Edit
                </button>
                <button onClick={() => {
                copyLink();
                toast('Shop link copied — ready to share');
              }} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2881" data-mp="3sw7nvsgkect4">
                  Share
                </button>
                <button onClick={() => toast('QR code downloaded')} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2882" data-mp="1yhkc75b9gey9">
                  QR code
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2883" data-mp="2i5b9am1vocrf">
            <button onClick={() => setPreview(true)} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-2884" data-mp="2i7nsf2jdgt62">
              Preview shop
            </button>
            {state === 'live' ? <a href={`/shop/${config.handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2885" data-mp="1tewap0hae825">
                <ExternalLinkIcon className="w-4 h-4" data-id="element-2886" data-mp="2h3xc43rpcixx" />
                View live shop
              </a> : <button onClick={() => {
            update({
              published: true
            });
            markPublished();
            toast(state === 'draft' ? 'Your shop is live' : 'Changes published');
          }} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2887" data-mp="2c7zwjzo9jgm7">
                <RocketIcon className="w-4 h-4" data-id="element-2888" data-mp="37ezevsj6w4v8" />
                {state === 'draft' ? 'Publish shop' : 'Publish changes'}
              </button>}
          </div>
        </div>

        {/* One quiet line about what to do next — the only status message */}
        {state !== 'live' && <p className="text-sm text-slate-500 mt-6" data-id="element-2889" data-mp="3le8ilsq7l6ib">
            {state === 'draft' ? 'Your shop is still a draft. Publish when you’re ready and customers can visit your link.' : 'You have unpublished changes. Customers won’t see them until you publish.'}
          </p>}
      </motion.section>

      {/* ------------------------------------------------------- Two assets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2890" data-mp="8v0m2n7xvent">
        <AssetCard title="Shop Page" description="Your AI-powered online shop" meta={primaryUrl} summary={`${config.publicName} theme · ${config.ai.tone}`} preview={<StorefrontViewport config={config} device="desktop" zoom="fit" className="w-full h-full" data-id="element-2892" data-mp="nr9v5ago94ls" />} primary={{
        label: 'Customize Shop',
        onClick: () => setOpen({
          kind: 'shop',
          tab: 'design'
        })
      }} secondary={[{
        label: 'Preview',
        onClick: () => setPreview(true)
      }, {
        label: 'Open shop',
        href: `/shop/${config.handle}`
      }, {
        label: copied === 'link' ? 'Copied' : 'Copy link',
        onClick: copyLink
      }]} data-id="element-2891" data-mp="czqge7my8l6m" />

        <AssetCard title="Website Chat" description="Your AI assistant on your website" meta={widget.domains[0] ?? 'No website connected yet'} summary={widget.domains.length > 0 ? 'Installed' : 'Not installed'} preview={
      // Rendered at page size, then scaled like a thumbnail so the chat
      // keeps its real proportions.
      <div className="w-full h-full overflow-hidden" data-id="element-2894" data-mp="2vz53i3owyo57">
              <div style={{
          width: 1400,
          height: 520,
          transform: 'scale(0.43)',
          transformOrigin: 'top left'
        }} data-id="element-2895" data-mp="3tm40ygnfk045">
                <WidgetPreview widget={widget} siteName={config.publicName} state="open" data-id="element-2896" data-mp="3jn50hlctjas8" />
              </div>
            </div>} primary={{
        label: 'Customize Chat',
        onClick: () => setOpen({
          kind: 'widget'
        })
      }} secondary={[{
        label: 'Test chat',
        onClick: () => setOpen({
          kind: 'widget'
        })
      }, {
        label: 'Installation',
        onClick: () => setOpen({
          kind: 'widget'
        })
      }]} data-id="element-2893" data-mp="3i7ty6xe6m02k" />
      </div>

      {/* --------------------------------------------- SEO & Sharing (optional) */}
      <motion.section initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease: 'easeOut'
    }} className="rounded-[28px] bg-white border border-slate-200/70 p-6 lg:p-7 mt-6" data-id="element-2897" data-mp="gcyjlzpapbb4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-2898" data-mp="3ottfgv7odvfp">
          <div className="min-w-0 flex-1" data-id="element-2899" data-mp="3tseeirb6y92k">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-2900" data-mp="3trd5esdqjwnh">
              <h3 className="font-display font-bold text-base text-dark-navy" data-id="element-2901" data-mp="3a5b85ar2qrzq">
                Search &amp; Sharing
              </h3>
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${seo.ready ? 'text-success' : 'text-slate-400'}`} data-id="element-2902" data-mp="3662hierbza8e">
                {seo.ready && <CheckIcon className="w-3.5 h-3.5" data-id="element-2903" data-mp="3f4edyxevp28r" />}
                {seoStatusLabel(seo)}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1.5" data-id="element-2904" data-mp="3t2ujil108mpb">
              Control how your shop appears on Google and when shared.
            </p>
          </div>
          <button onClick={() => setSeoOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:border-primary-indigo hover:text-primary-indigo transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2905" data-mp="6ob54oc157iu">
            Manage Search &amp; Sharing
            <ArrowRightIcon className="w-4 h-4" data-id="element-2906" data-mp="25768nhj391g5" />
          </button>
        </div>
      </motion.section>

      {/* Prototype-only: flip plans to see the branding gate in both states */}
      <div className="flex items-center gap-2 mt-8" data-id="element-2907" data-mp="2a46gnxzi0e9w">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-2908" data-mp="2ey0w2p82m736">
          Simulate plan
        </span>
        <div className="inline-flex p-1 bg-slate-50 rounded-xl" data-id="element-2909" data-mp="2jvas4npytgzz">
          {['Growth', 'Scale'].map(p => <button key={p} type="button" onClick={() => subscribePackage(p)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors duration-150 ease-out ${currentPlanId === p ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-2910" data-mp="1maeufmielciy">
              {p}
            </button>)}
        </div>
      </div>

      {preview && <ShopPreviewOverlay config={config} onClose={() => setPreview(false)} data-id="element-2911" data-mp="3cjykf394t6n1" />}
      {domainOpen && <DomainModal current={config.customDomain} talabUrl={talabUrl} shopHref={`/shop/${config.handle}`} onClose={() => setDomainOpen(false)} onSave={customDomain => {
      update({
        customDomain
      });
      if (!customDomain) {
        setDomainOpen(false);
        toast('Using your Talab link');
      }
    }} data-id="element-2912" data-mp="10wl7hroldijq" />}
      {seoOpen && <SeoModal config={config} primaryUrl={primaryUrl} onChange={patch => update({
      seo: {
        ...config.seo,
        ...patch
      }
    })} onClose={() => setSeoOpen(false)} data-id="element-2913" data-mp="d6qxdhvnq836" />}
      {upgrade && <UpgradePlanModal initialPlan="Scale" onClose={() => setUpgrade(false)} data-id="element-2914" data-mp="22lyq4ve926d4" />}
    </div>;
}

/* ---------------------------------------------------------------- Pieces */

function StatusChip({
  state
}: {
  state: 'draft' | 'changes' | 'live';
}) {
  const map = {
    live: {
      label: 'Live',
      cls: 'bg-success/10 text-success'
    },
    changes: {
      label: 'Changes ready',
      cls: 'bg-warning/10 text-warning'
    },
    draft: {
      label: 'Draft',
      cls: 'bg-slate-100 text-slate-500'
    }
  }[state];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${map.cls}`} data-id="element-2915" data-mp="eqacxl8v290s">
      <span className="w-1.5 h-1.5 rounded-full bg-current" data-id="element-2916" data-mp="4r1ot90ofomc" />
      {map.label}
    </span>;
}
function SmallAction({
  icon: Icon,
  label,
  onClick,
  href
}: {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
  href?: string;
}) {
  const cls = 'inline-flex items-center gap-1.5 px-3 py-3 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-primary-indigo transition-colors duration-150 ease-out';
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} data-id="element-2917" data-mp="1ckl3apj6t2lo">
        {Icon && <Icon className="w-4 h-4" data-id="element-2918" data-mp="17j5kl44hf86g" />}
        {label}
      </a>;
  return <button onClick={onClick} className={cls} data-id="element-2919" data-mp="1li5642wp76ye">
      {Icon && <Icon className="w-4 h-4" data-id="element-2920" data-mp="1hhlbb5zlkfff" />}
      {label}
    </button>;
}
interface Action {
  icon?: React.ElementType;
  label: string;
  onClick?: () => void;
  href?: string;
}
function AssetCard({
  title,
  description,
  meta,
  summary,
  preview,
  primary,
  secondary
}: {
  title: string;
  description: string;
  meta: string;
  summary: string;
  preview: React.ReactNode;
  primary: {
    label: string;
    onClick: () => void;
  };
  secondary: Action[];
}) {
  return <motion.section initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.24,
    ease: 'easeOut'
  }} className="rounded-[28px] bg-white border border-slate-200/80 overflow-hidden flex flex-col" data-id="element-2921" data-mp="xsaufoo47ofq">
      <button onClick={primary.onClick} aria-label={primary.label} className="group relative block w-full h-64 lg:h-72 bg-slate-50 overflow-hidden text-left" data-id="element-2922" data-mp="zwacidh6vdlg">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2923" data-mp="1408a2jqv8gce">{preview}</div>
        <span className="absolute inset-0 bg-dark-navy/0 group-hover:bg-dark-navy/[0.04] transition-colors duration-150 ease-out" data-id="element-2924" data-mp="18y9vhbbrkrr1" />
      </button>

      <div className="p-6 lg:p-8 flex-1 flex flex-col" data-id="element-2925" data-mp="1iumkhvwd6hfg">
        <h3 className="font-display font-bold text-lg text-dark-navy" data-id="element-2926" data-mp="1it1qc7dyp0i4">
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-1.5" data-id="element-2927" data-mp="1nnz7y2pb4i6m">{description}</p>
        <p className="text-sm font-semibold text-slate-600 mt-4 truncate" data-id="element-2928" data-mp="1rm6g495pgb67">
          {meta}
        </p>
        <p className="text-xs text-slate-400 mt-1" data-id="element-2929" data-mp="1wo457k0se072">{summary}</p>

        <div className="flex flex-wrap items-center gap-x-1 gap-y-2 mt-7" data-id="element-2930" data-mp="21l4cfy9dh8zr">
          <button onClick={primary.onClick} className="inline-flex items-center gap-2 px-5 py-3 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out mr-2" data-id="element-2931" data-mp="2bpkp6irk29zc">
            {primary.label}
            <ArrowRightIcon className="w-4 h-4" data-id="element-2932" data-mp="21t7xtrjhg87r" />
          </button>
          {secondary.map(a => <SmallAction key={a.label} {...a} data-id="element-2933" data-mp="1xs5tiwapl5yx" />)}
        </div>
      </div>
    </motion.section>;
}
function ShopPreviewOverlay({
  config,
  onClose
}: {
  config: React.ComponentProps<typeof StorefrontViewport>['config'];
  onClose: () => void;
}) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  return <div className="fixed inset-0 z-[70] bg-dark-navy/60 backdrop-blur-sm flex flex-col" data-id="element-2934" data-mp="2gnma798jkjdg">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white" data-id="element-2935" data-mp="3d6tj31m81po0">
        <p className="text-sm font-semibold text-dark-navy truncate" data-id="element-2936" data-mp="38fbkgk7o8167">
          Preview · talab.app/{config.handle}
        </p>
        <div className="inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-2937" data-mp="12w2taj2nm6ik">
          {(['desktop', 'mobile'] as const).map(d => <button key={d} onClick={() => setDevice(d)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors duration-150 ease-out ${device === d ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500'}`} data-id="element-2938" data-mp="yselbza418ws">
              {d}
            </button>)}
        </div>
        <button onClick={onClose} aria-label="Close preview" className="p-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-2939" data-mp="1hjxpwajs8v0x">
          <XIcon className="w-5 h-5" data-id="element-2940" data-mp="17tuvq70xdb10" />
        </button>
      </div>
      <div className="flex-1 min-h-0 bg-slate-100" data-id="element-2941" data-mp="25aa05s2i33uo">
        <StorefrontViewport config={config} device={device} zoom="fit" className="w-full h-full" data-id="element-2942" data-mp="25buxfpg790w8" />
      </div>
    </div>;
}